/**
 * accountPopup.ts — Accounts S8b: the New account popup's pure rules (spec 2
 * §3b; rulings of 2026-10-27). The popup (NewAccountPopup.tsx) renders them;
 * every number it shows is a server read's — nothing here counts records.
 */
import type { SalesAccountNameMatch, SalesAccountRename, SalesContactAtDomain } from '../../../../api/types'
import { compactMoney } from '../../../../lib/money'
import { fmtDate } from '../../api'
import { plural } from './accountMergeActions'

/** Ruling 3: a one-click title-case suggestion ONLY when the input has letters
 *  and none of them is upper case ("bell" → "Bell", "rolls royce ltd" →
 *  "Rolls Royce Ltd"). Anything else is saved exactly as typed: null. */
export function titleCaseSuggestion(input: string): string | null {
  const t = input.trim()
  if (!/[a-z]/.test(t) || t !== t.toLowerCase()) return null
  const out = t.replace(/(^|[\s\-'.&/(])([a-z])/g, (_, sep: string, ch: string) => sep + ch.toUpperCase())
  return out === t ? null : out
}

/** The website as a plain domain — the server's AccountDomainSQL, mirrored:
 *  lower case; http(s):// and www. stripped; any path dropped. */
export function websiteDomain(input: string): string {
  return input.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
}

/** Ruling 4: phone is stored exactly as typed; this is DISPLAY only, UK
 *  default. A UK number (11 digits from 0, or +44 and 10) is grouped; anything
 *  it cannot parse is shown exactly as typed. */
export function formatPhoneUK(raw: string): string {
  const t = raw.trim()
  const d = t.replace(/[\s\-().]/g, '')
  const intl = /^\+44\d{10}$/.test(d)
  if (!intl && !/^0\d{10}$/.test(d)) return t
  const national = intl ? d.slice(3) : d.slice(1)
  let groups: string[]
  if (national.startsWith('20')) groups = [national.slice(0, 2), national.slice(2, 6), national.slice(6)]
  else groups = [national.slice(0, 4), national.slice(4)]
  return intl ? `+44 ${groups.join(' ')}` : `0${groups.join(' ')}`
}

/** The card's three cases (spec §3b.2): in use (contacts or open deals) →
 *  open it; sparse (no contacts and no website) → rename it; an account with
 *  only a website is existing → open it. */
export type MatchKind = 'in_use' | 'sparse' | 'existing'
export function matchKind(m: SalesAccountNameMatch): MatchKind {
  if (m.contactCount > 0 || m.openDealCount > 0) return 'in_use'
  if (!m.website) return 'sparse'
  return 'existing'
}

/** The first match the user has not set aside ("It's a different company"). */
export function activeMatch(items: SalesAccountNameMatch[], dismissed: ReadonlySet<string>): SalesAccountNameMatch | null {
  return items.find(m => !dismissed.has(m.accountId)) ?? null
}

/** The in-use card's line: "This looks like X, which is already an account ·
 *  2 contacts · 1 open deal, £240k". No owner (register row 242). */
export function inUseLine(m: SalesAccountNameMatch): string {
  const parts = [`This looks like ${m.name}, which is already an account`, plural(m.contactCount, 'contact')]
  parts.push(m.openDealCount > 0 ? `${plural(m.openDealCount, 'open deal')}, ${compactMoney(m.openValueBasePence, 'GBP')}` : 'no open deals')
  return parts.join(' · ')
}

/** The sparse card's line, naming the latest won deal when there is one. */
export function sparseLine(m: SalesAccountNameMatch): string {
  const head = `Is this the same company as "${m.name}"? That account has no contacts or website`
  const w = m.latestWonDeal
  if (m.wonDealCount === 0 || !w) return `${head}. Renaming it keeps one record instead of two.`
  const when = w.closedAt ? `, ${fmtDate(w.closedAt).replace(/ \d{4}$/, '')}` : ''
  return `${head}, but ${plural(m.wonDealCount, 'won deal')} (${w.title}, ${compactMoney(w.valuePence, w.currency || 'GBP')}${when}). Renaming it keeps the deal history in one place.`
}

/** Ruling 3 of the recon: a contact at the website's domain is listed with
 *  its switch ON when it has no company and no account, OFF otherwise
 *  ("currently at {company}"). A contact already on `targetId` is not listed. */
export interface LinkRow { contact: SalesContactAtDomain; defaultOn: boolean; note: string }
export function linkRows(items: SalesContactAtDomain[], targetId: string | null): LinkRow[] {
  return items
    .filter(c => !targetId || c.accountId !== targetId)
    .map(c => {
      const at = c.accountName || c.company
      return at
        ? { contact: c, defaultOn: false, note: `currently at ${at}` }
        : { contact: c, defaultOn: true, note: 'currently has no company' }
    })
}

/** "Marco Bell", "Marco Bell and Rita Ring", "A, B and C". */
export function namesPhrase(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The footer's live summary (spec §3b.5), without the owner (row 242). */
export function liveSummary(p: { kind: 'create' | 'rename' | 'open'; name: string; oldName?: string; domain: string; linkNames: string[] }): string {
  const name = p.name.trim()
  if (p.kind === 'open') return `Opens ${p.oldName ?? name}. Nothing is created or changed.`
  if (p.kind === 'rename') {
    let s = `Renames "${p.oldName}" to ${name}`
    if (p.domain) s += ` and adds ${p.domain}`
    if (p.linkNames.length > 0) s += `, then links ${namesPhrase(p.linkNames)}`
    return `${s}. No second account is created.`
  }
  const parts = [`Creates ${name || 'the account'}`]
  if (p.domain) parts.push(p.domain)
  if (p.linkNames.length > 0) parts.push(`links ${namesPhrase(p.linkNames)}`)
  return `${parts.join(' · ')}.`
}

/** A rename record's display label, from what it changed (ruling 3 of the
 *  S8b round): a changed name reads as the rename; an unchanged name — the
 *  record a create's links ride on — reads "Linked {n} contacts". */
export function renameLabel(rec: Pick<SalesAccountRename, 'nameBefore' | 'nameAfter' | 'links'>): string {
  if (rec.nameBefore !== rec.nameAfter) return `Renamed "${rec.nameBefore}" to ${rec.nameAfter}`
  return `Linked ${plural(rec.links.length, 'contact')}`
}

/** The Undo's toast, from the same record. */
export function renameUndoneLabel(rec: Pick<SalesAccountRename, 'nameBefore' | 'nameAfter' | 'links'>): string {
  if (rec.nameBefore !== rec.nameAfter) return `Rename undone: "${rec.nameAfter}" is "${rec.nameBefore}" again`
  return `Links undone: ${plural(rec.links.length, 'contact')} unlinked`
}
